import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/**
 * The variant-choice dialog opened from the PRAGER'S product card
 * (blocks/_mx-pragers-product-card.liquid) when a product has more than one
 * variant. The card's cart CTA opens it instead of submitting, and the
 * customer must pick a variant before the add-to-cart submit unlocks — no
 * first-available variant is ever added silently.
 *
 * Extends DialogComponent with the behaviours the base class cannot know:
 *
 * - on connect it locks the submit (unless a radio is already checked, which
 *   a bfcache restore can produce) and turns the fly-to-cart animation off
 *   for the inner add-to-cart-component. product-form.js only suppresses
 *   that animation inside Horizon's own `.quick-add-modal`; from this dialog
 *   it would fly a stale first-variant image underneath the top layer.
 *   Plain attribute writes, not disable()/dataset on the upgraded component,
 *   because this module may execute before product-form.js defines it.
 * - a picked radio copies its variant id into the form's hidden `id` input.
 *   The hidden input, not the radios, is what product-form.js submits and
 *   reads (its `ref="variantId"` is a required ref there), so the radios stay
 *   pure UI with a name /cart/add ignores.
 * - a successful add — the CartAddEvent bubbling up from the inner
 *   product-form-component — closes the dialog, the same contract
 *   QuickAddDialog has (not inherited from it: quick-add.js doesn't export
 *   the class, and its fetch/morph pipeline is the part this dialog exists
 *   to avoid). On a failed add the event carries didError and the dialog
 *   stays open, showing the form's own error text. Listening on `this`
 *   rather than `document` means a dialog only reacts to its own form's
 *   adds, never to another card's.
 */
class MxVariantDialog extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);

    this.querySelector('add-to-cart-component')?.setAttribute('data-add-to-cart-animation', 'false');

    if (!this.querySelector('.mx-pragers-variant-dialog__input:checked')) {
      this.#submitButton?.setAttribute('disabled', '');
    }
  }

  disconnectedCallback() {
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd);
    super.disconnectedCallback();
  }

  /** @returns {HTMLButtonElement | null} */
  get #submitButton() {
    return this.querySelector('button[ref="addToCartButton"]');
  }

  /**
   * Copies the picked variant into the hidden `id` input and unlocks the
   * add-to-cart button.
   *
   * @param {Event & { target: HTMLElement }} event - Change event from a variant radio.
   */
  handleVariantSelect(event) {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || !radio.checked) return;

    const variantIdInput = this.querySelector('input[name="id"]');
    if (variantIdInput instanceof HTMLInputElement) {
      variantIdInput.value = radio.value;
    }

    this.#submitButton?.removeAttribute('disabled');
  }

  /**
   * Closes the dialog once its own form has added to the cart.
   *
   * @param {Event & { detail?: { data?: { didError?: boolean } } }} event - The bubbling CartAddEvent.
   */
  #handleCartAdd = (event) => {
    if (event.detail?.data?.didError) return;
    this.closeDialog();
  };
}

if (!customElements.get('mx-variant-dialog')) {
  customElements.define('mx-variant-dialog', MxVariantDialog);
}
